/**
 * ProgressBar — fremdriftslinje (#152, mangler i MAPPING.md §3).
 *
 * 8 px spor i `bg-track` med fyllet fra `--stell-progress` (grønn → lime,
 * likt i begge temaer) via `bg-progress`-utilityen i theme.css. Brukes på
 * Hjem, Premier, Tavle, Admin, Oppsett og Kjøkken i senere steg.
 *
 * `role="progressbar"` med min/maks/nå, og `label` som tilgjengelig navn.
 * Verdien klemmes til [0, max], så en overskutt poengsum ikke tegner utenfor
 * sporet. Bredden er inline style fordi den er en kjøretidsverdi.
 */
import type { ReactNode } from 'react';

interface ProgressBarProps {
  value: number;
  max?: number;
  label: string;
  className?: string;
  'data-testid'?: string;
}

export function ProgressBar({
  value,
  max = 100,
  label,
  className,
  'data-testid': testId = 'progress-bar',
}: ProgressBarProps): ReactNode {
  const safeMax = max > 0 ? max : 1;
  const clamped = Math.min(Math.max(value, 0), safeMax);
  const pct = (clamped / safeMax) * 100;
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={safeMax}
      aria-valuenow={clamped}
      data-testid={testId}
      className={['h-2 w-full overflow-hidden rounded-pill bg-track', className]
        .filter(Boolean)
        .join(' ')}
    >
      <div
        data-testid={`${testId}-fill`}
        className="h-full rounded-pill bg-progress"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}
